import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStoreByName } from "./use-stores.hooks";
import { fetchBookContent, fetchBookVersions, BookContentResponse } from "../services/api/books.service";
import { runInAction } from "mobx";
import { useEffect, useRef } from "react";

/**
 * TanStack Query v5 hooks for books endpoints
 * Following the work plan: services → TanStack Query → MobX store sync
 * 
 * Rules:
 * 1. Do not modify existing stores
 * 2. Do not modify existing services  
 * 3. Each hook uses clear queryKey
 * 4. Always update MobX store using useEffect (v5 pattern)
 * 5. Cache is always persistent (staleTime: Infinity, gcTime: Infinity)
 * 6. Version checking clears cache automatically when content changes
 */

/**
 * Transforms raw book content based on book type
 * Same logic as in books.controller.ts fetchContent method
 * Note: booksStore parameter is not used but kept for consistency with original signature
 */
const transformBookContent = (content: any, bookName: string, _booksStore?: any) => {
  // Normalize bookName for switch case (handle both lowercase and original case)
  const normalized = bookName.toLowerCase();
  switch (normalized) {
    case 'mishna':
      return content.map((book: any) => ({
        id: book.id,
        label: book.name,
        children: book.chapters.map((chapter: any) => ({
          id: chapter.id,
          label: chapter.name,
          children: chapter.mishnayot.map((mishna: any) => ({
            id: mishna.id,
            label: mishna.name,
            text: mishna.text
          }))
        }))
      }));

    case 'torah':
      // Torah navigates חומש → פרק → פסוק. The parasha is a bookmark carried
      // on each chapter plus a jump index on the chumash — it is deliberately
      // not a navigation level, because chapters number continuously through
      // the chumash (בראשית runs פרק א–נ) and do not restart per parasha.
      return content.map((book: any) => ({
        id: book.id,
        label: book.name,
        parshiot: book.parshiot || [],
        children: (book.chapters || []).map((chapter: any) => ({
          id: chapter.id,
          label: chapter.name_he,
          perek: chapter.index,
          parashaLabel: chapter.parasha_label,
          parashaId: chapter.parashot?.[0]?.id,
          children: chapter.verses.map((verse: any) => ({
            id: verse.id,
            label: verse.name_he || `פסוק ${verse.index}`,
            // Needed to resolve which parasha the position falls inside:
            // a straddling chapter changes parasha partway through.
            pasuk: verse.index,
            text: verse.text
          }))
        }))
      }));

    case 'neviim':
    case 'ketuvim':
      // Transform the Neviim/Ketuvim data structure (categories with books)
      return content.map((category: any) => ({
        id: category.id,
        label: category.name,
        children: category.books.map((book: any) => ({
          id: book.id,
          label: book.name,
          children: book.chapters.map((chapter: any) => ({
            id: chapter.id,
            label: chapter.name_he,
            children: chapter.verses.map((verse: any) => ({
              id: verse.id,
              label: verse.name_he || `פסוק ${verse.index}`,
              text: verse.text
            }))
          }))
        }))
      }));

    case 'siddur_ashkenaz':
      // Transform the Siddur data structure (gates with parts, topics, prayers, sub_sections)
      // console.log('🔍 Siddur fetchContent - Raw content:', content);
      // console.log('🔍 Siddur fetchContent - First gate structure:', content[0]);
      // console.log('🔍 Siddur fetchContent - First gate chapters:', content[0]?.chapters);
      // console.log('🔍 Siddur fetchContent - First chapter mishnayot:', content[0]?.chapters?.[0]?.mishnayot);
      // console.log('🔍 Siddur fetchContent - First mishna text:', content[0]?.chapters?.[0]?.mishnayot?.[0]?.text);
      
      // בדיקה שהנתונים קיימים ותואמים למבנה הצפוי
      if (!content || !Array.isArray(content)) {
        console.error('❌ Siddur content is not an array:', content);
        return [];
      }
      
      return content.map((gate: any) => {
        // בדיקה שהשער קיים
        if (!gate || !gate.name_he) {
          console.error('❌ Invalid gate structure:', gate);
          return null;
        }
        
        return {
          id: gate.id,
          label: gate.name_he,
          children: (gate.topics || []).map((topic: any) => {
            // בדיקה שהנושא קיים
            if (!topic || !topic.name_he) {
              console.error('❌ Invalid topic structure:', topic);
              return null;
            }
            
            return {
              id: topic.id,
              label: topic.name_he,
              children: (topic.subTopics || []).map((subTopic: any) => {
                // בדיקה שהתת-נושא קיים
                if (!subTopic || !subTopic.name_he) {
                  console.error('❌ Invalid subTopic structure:', subTopic);
                  return null;
                }
                
                return {
                  id: subTopic.id,
                  label: subTopic.name_he,
                  children: (subTopic.parts || []).map((part: any) => {
                    // בדיקה שהחלק קיים
                    if (!part || !part.name_he) {
                      console.error('❌ Invalid part structure:', part);
                      return null;
                    }
                    
                    return {
                      id: part.id,
                      label: part.name_he,
                      text: part.text,
                      // הוספת sub_sections כ-children לרמה חמישית
                      children: part.sub_sections ? part.sub_sections.map((subSection: any) => ({
                        id: subSection.id,
                        label: subSection.name_he,
                        text: subSection.text
                      })) : undefined
                    };
                  }).filter(Boolean) // הסרת null values
                };
              }).filter(Boolean) // הסרת null values
            };
          }).filter(Boolean) // הסרת null values
        };
      }).filter(Boolean); // הסרת null values

    default:
      return content;
  }
};

/** Key part used when neither the server nor localStorage can tell us a version. */
export const UNVERSIONED = "unversioned";

/**
 * Decides which content version a book query should be keyed by.
 *
 * - The live version from /book-versions wins whenever we have it.
 * - While that request is still in flight we return undefined, which keeps the
 *   book query disabled: better a moment of skeleton than a fetch under a key
 *   we are about to abandon.
 * - If the request failed (offline, server blip) we fall back to the version
 *   stored from the last successful load, so the persisted book still opens.
 * - A client that has never loaded this book and cannot reach /book-versions
 *   fetches under UNVERSIONED rather than showing nothing at all.
 */
export const resolveBookVersion = (
  liveVersion: string | undefined,
  versionsPending: boolean,
  storedVersion: string | null
): string | undefined => {
  if (liveVersion) return liveVersion;
  if (versionsPending) return undefined;
  return storedVersion || UNVERSIONED;
};

/**
 * Fetch book content with TanStack Query
 * Cache is always persistent (staleTime: Infinity, gcTime: Infinity)
 * All books including Siddur are cached
 */
export function useBookContent(bookName: string, forceRefresh = false) {
  const booksStore = useStoreByName("booksStore");
  const queryClient = useQueryClient();

  // Normalize bookName to avoid unnecessary re-renders
  const normalizedBookName = bookName?.toLowerCase() || '';
  const storedVersionKey = `book_version_${normalizedBookName}`;

  /**
   * LIVE version check, independent of the cached book.
   *
   * The book itself is cached with staleTime: Infinity and persisted to
   * localStorage, so it never refetches on its own. This asks the server for
   * just the version strings, about 200 bytes, on every load. That is the only
   * way the client can learn content changed without downloading the content
   * it is trying to decide about.
   */
  const versionsQuery = useQuery({
    queryKey: ["books", "versions"],
    queryFn: fetchBookVersions,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const liveVersion = versionsQuery.data?.[normalizedBookName];
  const resolvedVersion = normalizedBookName
    ? resolveBookVersion(
        liveVersion,
        versionsQuery.isPending,
        localStorage.getItem(storedVersionKey)
      )
    : undefined;

  /**
   * The version is PART OF THE QUERY KEY.
   *
   * Every earlier scheme kept one key per book and tried to notice a version
   * change and then invalidate, refetch, and record the new version in the
   * right order. Two separate effects did that, one from the live version and
   * one from the version inside the cached payload, and they could disagree,
   * race, or record the new version before a refetch had actually succeeded.
   * A phone could end up with stored === live and an old payload it would
   * never replace, and clearing cookies does not touch localStorage.
   *
   * With the version in the key there is nothing to notice. A bump gives a new
   * key, the old persisted entry is simply never read again, and the book
   * fetches fresh. There is no invalidation to get wrong.
   */
  const query = useQuery({
    queryKey: ["books", "content", normalizedBookName, resolvedVersion],
    queryFn: () => fetchBookContent(normalizedBookName),
    enabled: !!normalizedBookName && !!resolvedVersion,
    staleTime: Infinity, // Cache is always fresh - never refetch automatically
    gcTime: Infinity, // Cache is never garbage collected
    retry: 2,
  });

  // Transform and sync with MobX store when data changes
  // Use ref to track last processed dataUpdatedAt to avoid infinite loops
  const lastProcessedRef = useRef<{ bookName: string; dataUpdatedAt: number } | null>(null);

  /**
   * Drop every other version of this book from the cache. Books are megabytes
   * and the persisted cache lives in localStorage, so letting old versions pile
   * up would eventually stop the persister from writing at all. This also
   * removes entries written by the previous three-part key.
   */
  useEffect(() => {
    if (!normalizedBookName || !resolvedVersion) return;
    queryClient.removeQueries({
      queryKey: ["books", "content", normalizedBookName],
      predicate: (q) => q.queryKey[3] !== resolvedVersion,
    });
  }, [normalizedBookName, resolvedVersion, queryClient]);


  useEffect(() => {
    
    if (!normalizedBookName) {
      lastProcessedRef.current = null;
      return; // Don't process if no book selected
    }
    
    // Only process if we have new data that hasn't been processed yet
    // AND the data matches the currently selected book
    if (query.data && query.dataUpdatedAt) {
      const currentBookName = booksStore.selectedBook?.name_en?.toLowerCase();
      
      
      // CRITICAL: Only process data if it matches the selected book
      // This prevents using cached data from a different book
      if (currentBookName && currentBookName !== normalizedBookName) {
        console.warn(`⚠️ Data mismatch: selected book is "${currentBookName}" but query is for "${normalizedBookName}". Skipping update.`);
        return;
      }
      
      // Extract data from response
      const bookResponse = query.data as BookContentResponse;
      const bookData = bookResponse.data;
      
      // Debug: Log version from server

      // Remember the version this payload was loaded under. It is only the
      // offline fallback for the query key: when /book-versions cannot be
      // reached, the persisted book under this version still opens.
      if (resolvedVersion && resolvedVersion !== UNVERSIONED) {
        localStorage.setItem(storedVersionKey, resolvedVersion);
      }

      const currentKey = {
        bookName: normalizedBookName,
        dataUpdatedAt: query.dataUpdatedAt
      };
      
      // If the store has no levels but we are holding data, sync it whatever
      // the marker says. setSelectedBook clears the levels on a book change,
      // and that clearing does not reset this ref, so a store that had already
      // been marked processed would otherwise stay empty forever — the
      // selectors would sit on their loading skeletons with nothing to show
      // and no path back. Cheap insurance: an empty store plus data in hand is
      // always worth writing.
      const storeIsEmpty = booksStore.levelOne.length === 0;

      // Skip if we've already processed this exact data
      // BUT: if lastProcessedRef was reset (null), always process (this happens after version change)
      if (
        !storeIsEmpty &&
        lastProcessedRef.current !== null &&
        lastProcessedRef.current?.bookName === currentKey.bookName &&
        lastProcessedRef.current?.dataUpdatedAt === currentKey.dataUpdatedAt
      ) {
        return;
      }
      
      
      runInAction(() => {
        const transformedContent = transformBookContent(bookData, normalizedBookName, booksStore);
        const isBookChanged = currentBookName !== normalizedBookName;
        
        
        // Always update levels when data arrives - this matches the old controller behavior
        // The old fetchContent always called setLevels and setSelectedLevelOne after fetching
        if (isBookChanged) {
          // Clear all levels and selections when book changes
          booksStore.levelOne = [];
          booksStore.levelTwo = [];
          booksStore.levelThree = [];
          booksStore.levelFour = [];
          booksStore.levelFive = [];
          booksStore.selectedLevelOne = null;
          booksStore.selectedLevelTwo = null;
          booksStore.selectedLevelThree = null;
          booksStore.selectedLevelFour = null;
          booksStore.selectedLevelFive = null;
          booksStore.textEntities = [];
          booksStore.selectedTextEntity = null;
        }
        
        // Set new levels (matches old controller: this.setLevels(content))
        booksStore.setLevels(transformedContent);
        
        // Update cache in books array for backward compatibility
        const bookIndex = booksStore.books.findIndex((b: any) => b.name_en?.toLowerCase() === normalizedBookName);
        if (bookIndex !== -1) {
          booksStore.books[bookIndex].content = booksStore.levelOne;
        }
        
        booksStore.setIsLoading(false);
        lastProcessedRef.current = currentKey; // Mark as processed
      });
      
      // Set a default selectedLevelOne ONLY when there isn't one already.
      // This handles two cases safely:
      //   1. First load of a book (selectedLevelOne is null)
      //   2. Book switch (isBookChanged cleared selectedLevelOne to null above)
      // It must NOT override an existing user selection (which previously caused
      // navigation to /reading to silently reset Mishna to a hardcoded index).
      if (booksStore.levelOne.length > 0 && !booksStore.selectedLevelOne) {
        const targetLevelOne = booksStore.levelOne[0];

        if (targetLevelOne) {
          requestAnimationFrame(() => {
            runInAction(() => {
              booksStore.setSelectedLevelOne(targetLevelOne);
            });
          });
        }
      }
    }
    
    if (query.error) {
      console.error('❌ Query error', query.error);
      runInAction(() => {
        booksStore.setIsLoading(false);
        console.error(`Error loading book ${normalizedBookName}:`, query.error);
      });
    }
    
    // Waiting on /book-versions counts as loading too: the book query is
    // disabled until the key is known, so query.isLoading alone would be false.
    if (query.isLoading || !resolvedVersion) {
      runInAction(() => {
        booksStore.setIsLoading(true);
      });
    }
    // Note: booksStore is intentionally excluded from dependencies to avoid infinite loops
    // MobX observables can cause re-renders, but we only want to react to query state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, query.dataUpdatedAt, query.error, query.isLoading, normalizedBookName, resolvedVersion]);

  // Handle force refresh
  useEffect(() => {
    if (forceRefresh && normalizedBookName) {
      queryClient.invalidateQueries({ 
        queryKey: ["books", "content", normalizedBookName] 
      });
    }
  }, [forceRefresh, normalizedBookName, queryClient]);

  return query;
}

/**
 * Hook to refresh a specific book
 */
export function useRefreshBook() {
  const queryClient = useQueryClient();
  
  return (bookName: string) => {
    queryClient.invalidateQueries({ 
      queryKey: ["books", "content", bookName.toLowerCase()] 
    });
  };
}

/**
 * Hook to clear all book caches
 */
export function useClearBookCache() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: ["books", "content"] });
  };
}

